'use client';

import React, { useState } from 'react';
import { ChevronDown, ChevronUp, CheckCircle, Lock } from 'lucide-react';
import { MOCK_EXPENSES, EXPENSE_CATEGORIES, EXPENSE_STATUS_STYLES, formatUGX } from './expensesData';

interface Props {
  farmFilter: string;
  categoryFilter: string;
  search: string;
  canApprove?: boolean;
}

export default function ExpenseTable({ farmFilter, categoryFilter, search, canApprove = false }: Props) {
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const filtered = MOCK_EXPENSES.filter(e => {
    const matchFarm = !farmFilter || e.farm === farmFilter;
    const matchCat = !categoryFilter || e.category === categoryFilter;
    const q = search.toLowerCase();
    const matchSearch = !q || e.description.toLowerCase().includes(q) || e.vendor.toLowerCase().includes(q) || e.expenseNumber.toLowerCase().includes(q);
    return matchFarm && matchCat && matchSearch;
  });

  return (
    <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-gray-50 border-b border-gray-200">
          <tr>
            <th className="text-left px-4 py-3 font-semibold text-gray-600 text-xs">Ref #</th>
            <th className="text-left px-4 py-3 font-semibold text-gray-600 text-xs">Category</th>
            <th className="text-left px-4 py-3 font-semibold text-gray-600 text-xs">Description</th>
            <th className="text-left px-4 py-3 font-semibold text-gray-600 text-xs">Farm / Branch</th>
            <th className="text-left px-4 py-3 font-semibold text-gray-600 text-xs">Date</th>
            <th className="text-right px-4 py-3 font-semibold text-gray-600 text-xs">Amount</th>
            <th className="text-left px-4 py-3 font-semibold text-gray-600 text-xs">Status</th>
            {canApprove && <th className="text-left px-4 py-3 font-semibold text-gray-600 text-xs">Action</th>}
            <th className="px-4 py-3"></th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-50">
          {filtered.length === 0 && (
            <tr><td colSpan={canApprove ? 9 : 8} className="text-center py-12 text-gray-400 text-sm">No expenses found</td></tr>
          )}
          {filtered.map(exp => {
            const isExpanded = expandedId === exp.id;
            const cat = EXPENSE_CATEGORIES.find(c => c.value === exp.category);
            const st = EXPENSE_STATUS_STYLES[exp.status];
            return (
              <React.Fragment key={exp.id}>
                <tr className="hover:bg-gray-50 transition-colors cursor-pointer" onClick={() => setExpandedId(isExpanded ? null : exp.id)}>
                  <td className="px-4 py-3 font-mono text-xs font-semibold text-orange-700">{exp.expenseNumber}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${cat?.bg ?? 'bg-gray-100'} ${cat?.color ?? 'text-gray-600'}`}>
                      {cat?.label ?? exp.category}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <p className="text-xs font-semibold text-gray-900 max-w-[200px] truncate">{exp.description}</p>
                    <p className="text-xs text-gray-400">{exp.vendor}</p>
                  </td>
                  <td className="px-4 py-3">
                    <p className="text-xs text-gray-700">{exp.farm}</p>
                    <p className="text-xs text-gray-400">{exp.branch}</p>
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-600">{exp.date}</td>
                  <td className="px-4 py-3 text-right text-xs font-bold text-gray-900">{formatUGX(exp.amount)}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${st.bg} ${st.text}`}>{st.label}</span>
                  </td>
                  {canApprove && (
                    <td className="px-4 py-3" onClick={e => e.stopPropagation()}>
                      {exp.status === 'pending_approval' ? (
                        <button
                          className="flex items-center gap-1 px-2.5 py-1 bg-green-600 text-white text-xs font-semibold rounded-lg hover:bg-green-700 transition-colors"
                          title="Approve expense"
                        >
                          <CheckCircle size={12} />
                          Approve
                        </button>
                      ) : (
                        <span className="text-xs text-gray-300">—</span>
                      )}
                    </td>
                  )}
                  <td className="px-4 py-3">
                    {isExpanded ? <ChevronUp size={14} className="text-gray-400" /> : <ChevronDown size={14} className="text-gray-400" />}
                  </td>
                </tr>
                {isExpanded && (
                  <tr className="bg-orange-50/30">
                    <td colSpan={canApprove ? 9 : 8} className="px-6 py-4">
                      <div className="grid grid-cols-3 gap-6 text-xs">
                        <div className="space-y-1">
                          <p className="font-bold text-gray-700 mb-1.5">Expense Details</p>
                          {exp.quantity && <div className="flex justify-between text-gray-600"><span>Quantity</span><span className="font-semibold">{exp.quantity} {exp.unit}</span></div>}
                          {exp.unitCost && <div className="flex justify-between text-gray-600"><span>Unit Cost</span><span className="font-semibold">{formatUGX(exp.unitCost)}</span></div>}
                          <div className="flex justify-between text-gray-800 font-bold"><span>Total</span><span>{formatUGX(exp.amount)}</span></div>
                        </div>
                        <div className="space-y-1">
                          <p className="font-bold text-gray-700 mb-1.5">Payment</p>
                          <div className="flex justify-between text-gray-600"><span>Method</span><span>{exp.paymentMethod}</span></div>
                          <div className="flex justify-between text-gray-600"><span>Receipt Ref</span><span className="font-mono">{exp.receiptRef || '—'}</span></div>
                          <div className="flex justify-between text-gray-600">
                            <span>Approved By</span>
                            <span className="flex items-center gap-1">
                              {!canApprove && exp.status === 'pending_approval' && <Lock size={10} className="text-gray-400" />}
                              {exp.approvedBy}
                            </span>
                          </div>
                        </div>
                        <div className="space-y-1">
                          <p className="font-bold text-gray-700 mb-1.5">Recorded By</p>
                          <p className="text-gray-600">{exp.recordedBy}</p>
                          {exp.notes && <p className="text-gray-500 italic mt-2">{exp.notes}</p>}
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
      {!canApprove && filtered.some(e => e.status === 'pending_approval') && (
        <div className="flex items-center gap-2 px-4 py-2.5 bg-amber-50 border-t border-amber-100 text-xs text-amber-700">
          <Lock size={12} />
          <span>Some expenses are pending approval. Only Managers and Farm Owners can approve expenses.</span>
        </div>
      )}
    </div>
  );
}
